import { merge } from 'lodash';
import { AdditionalData, ReportActivityOptions, bianka } from '@fiverr-private/obs';
import { Click, ClickType, ElementType, Impression, PageView, Hover } from '@fiverr-private/bianka_js';
import { getContext } from '@fiverr-private/fiverr_context';

interface AdditionalDataType<T extends object> {
    type: AdditionalData<T>['type'];
    message?: Partial<AdditionalData<T>['message']>;
}

export interface PageViewTypeExtraData<T extends object> {
    event: {
        type: ReportActivityOptions<PageView, T>['event']['type'];
    };
    additionalData?: AdditionalDataType<T>;
}

export interface ClickTypeExtraData<T extends object> {
    event: {
        type: ReportActivityOptions<Click, T>['event']['type'];
        element: Partial<ReportActivityOptions<Click, T>['event']['element']>;
        clickType?: ReportActivityOptions<Click, T>['event']['clickType'];
        seller?: ReportActivityOptions<Click, T>['event']['seller'];
    };

    additionalData?: AdditionalDataType<T>;
}

export interface ImpressionTypeExtraData<T extends object> {
    event: {
        type: ReportActivityOptions<Impression, T>['event']['type'];
        component: Partial<ReportActivityOptions<Impression, T>['event']['component']>;
    };
    additionalData?: AdditionalDataType<T>;
}

const getBaseData = (pageName?: string) => {
    const { organizationId, pageCtxId } = getContext();
    return {
        event: {
            page: {
                name: pageName,
                ctxId: pageCtxId,
            },
            organization: {
                id: organizationId,
            },
        },
        additionalData: {
            message: {},
        },
    };
};

export const biEvent = {
    click: <T extends object>(pageName?: string, extraData?: ClickTypeExtraData<T>) => {
        const baseEvent = getBaseData(pageName);
        const baseData = {
            type: Click,
            event: {
                clickType: ClickType.LEFT,
                element: {
                    type: ElementType.BUTTON,
                },
            },
        } as ReportActivityOptions<Click, T>;
        const data = merge({}, baseEvent, baseData, extraData || {});
        bianka.reportActivity<Click, T>(data as ReportActivityOptions<Click, T>);
    },
    impression: <T extends object>(pageName?: string, extraData?: ImpressionTypeExtraData<T>) => {
        const baseEvent = getBaseData(pageName);
        const baseData = {
            type: Impression,
        } as ReportActivityOptions<Impression, T>;
        const data = merge({}, baseEvent, baseData, extraData || {});
        bianka.reportActivity<Impression, T>(data as ReportActivityOptions<Impression, T>);
    },
    hover: <T extends object>(pageName?: string, extraData?: ClickTypeExtraData<T>) => {
        const baseEvent = getBaseData(pageName);
        const baseData = {
            type: Hover,
            event: {
                element: {
                    type: ElementType.LINK,
                },
            },
        } as ReportActivityOptions<Hover, T>;
        const data = merge({}, baseEvent, baseData, extraData || {});
        bianka.reportActivity<Hover, T>(data as ReportActivityOptions<Hover, T>);
    },
    pageView: <T extends object>(pageName?: string, extraData?: PageViewTypeExtraData<T>) => {
        const baseEvent = getBaseData(pageName);

        const baseData = {
            type: PageView,
            event: {
                type: `${pageName}.show`,
            },
        } as ReportActivityOptions<Impression, T>;
        const data = merge({}, baseEvent, baseData, extraData || {});
        bianka.reportActivity<Impression, T>(data as ReportActivityOptions<Impression, T>);
    },
};
